import CircularProgress from '@mui/material/CircularProgress'
import { mailboxHooks } from 'features/messaging'
import { notificationUtils } from 'features/notifications'
import { type KeyboardEvent, useEffect, useState } from 'react'
import { a11y } from 'shared/styles'

import { api } from '../../api'
import css from './Note.module.css'

const isCommandEnter = (event: KeyboardEvent<HTMLTextAreaElement>) =>
	event.key === 'Enter' && (event.ctrlKey || event.metaKey)

export function Note() {
	const { id, isNotExist } = useContactId()

	return !isNotExist && <Form id={id} key={id} />
}

function Form({ id }: { id: string }) {
	const [value, setValue] = useState('')

	const noteQuery = api.useContactsNoteQuery(id, {
		skip: !id
	})
	const [send, sendOption] = api.useContactsNoteSaveMutation()
	const isReady = id && noteQuery.isSuccess && Boolean(noteQuery.currentData)
	const idDisabled = !isReady || sendOption.isLoading

	const savedValue = noteQuery.currentData?.value

	useEffect(() => {
		if (savedValue) {
			setValue(savedValue)
		}
	}, [savedValue])

	async function save() {
		if (value === savedValue) {
			return
		}

		const { data } = await send({
			id,
			value
		})

		if (data?.id) {
			notificationUtils.snackById('n3.37')
		}
	}

	return (
		<form className={css.form} onSubmit={save}>
			<textarea
				className={css.note}
				disabled={idDisabled}
				maxLength={3000}
				onBlur={save}
				onInput={event => setValue(event.currentTarget.value)}
				onKeyDown={event => {
					if (isCommandEnter(event)) {
						event.preventDefault()
						save()
					}
				}}
				placeholder='Leave notes'
				value={value}
			/>

			{idDisabled && <CircularProgress className={css.loader} size={20} />}

			<button className={a11y.hidden} disabled={idDisabled} type='submit'>
				Save
			</button>
		</form>
	)
}

function useContactId() {
	const conversationalist = mailboxHooks.useConversationalist()

	const { currentData: contact, isSuccess } = api.useContactByLinkedInIdQuery(
		conversationalist?.entityId ?? '',
		{
			skip: !conversationalist?.entityId
		}
	)

	return {
		id: contact?.id ?? '',
		isNotExist: isSuccess && contact === null
	}
}
